import * as HubActions from './hub-connection.actions';
import { catchError, filter, map, switchMap } from 'rxjs/operators';
import { of } from 'rxjs';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { HubConnectionService } from 'src/app/shared/services/realtime-connection.service';
import { PdGroups } from 'src/app/shared/services/realtime-constants';
import { IdentityService } from 'src/app/core/identity.service';

@Injectable()
export class HubConnectionEffects {
    constructor(
        private readonly _actions$: Actions,
        private readonly _identityService: IdentityService,
        private readonly _hubConnectionService: HubConnectionService
    ) {}

    connectToCoreHub$ = createEffect(() =>
        this._actions$.pipe(
            ofType(HubActions.connectToCoreHub),
            switchMap(() => (this._identityService.enforceOAuth ? this._identityService.isAuthenticated$ : of(true))),
            filter((auth) => auth),
            switchMap(() =>
                this._hubConnectionService.coreHub.startConnection().pipe(
                    map(() => HubActions.connectToCoreHubSuccess()),
                    catchError((error) => of(HubActions.connectToCoreHubFailure({ error })))
                )
            )
        )
    );

    disconnectFromCoreHub$ = createEffect(() =>
        this._actions$.pipe(
            ofType(HubActions.disconnectFromCoreHub),
            switchMap(() =>
                this._hubConnectionService.coreHub.stopConnection().pipe(
                    map(() => HubActions.disconnectFromCoreHubSuccess()),
                    catchError((error) => of(HubActions.disconnectFromCoreHubFailure({ error })))
                )
            )
        )
    );

    isHubConnectionActive$ = createEffect(() =>
        this._hubConnectionService.coreHub
            .isConnectionActive()
            .pipe(map((isActive) => HubActions.isHubConnectionActive({ isActive })))
    );

    connectToPdHub$ = createEffect(() =>
        this._actions$.pipe(
            ofType(HubActions.connectToPdHub),
            switchMap((action) => {
                if (this._identityService.enforceOAuth) {
                    return this._identityService.isAuthenticated$.pipe(
                        filter((auth) => auth),
                        map(() => action)
                    );
                } else {
                    return of(action);
                }
            }),
            switchMap((action) =>
                this._hubConnectionService.pdHub
                    .startConnection([
                        PdGroups.FACILITY + action.facilityId.toString(),
                        PdGroups.SYSTEM + action.facilitySystemId.toString()
                    ])
                    .pipe(
                        map(() => HubActions.connectToPdHubSuccess()),
                        catchError((error) => of(HubActions.connectToPdHubFailure({ error })))
                    )
            )
        )
    );

    disconnectFromPdHub$ = createEffect(() =>
        this._actions$.pipe(
            ofType(HubActions.disconnectFromPdHub),
            switchMap(() =>
                this._hubConnectionService.pdHub.stopConnection().pipe(
                    map(() => HubActions.disconnectFromPdHubSuccess()),
                    catchError((error) => of(HubActions.disconnectFromPdHubFailure({ error })))
                )
            )
        )
    );

    isPdHubConnectionActive$ = createEffect(() =>
        this._hubConnectionService.pdHub
            .isConnectionActive()
            .pipe(map((isActive) => HubActions.isPdHubConnectionActive({ isActive })))
    );

    connectToTimecardHub$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(HubActions.connectToTimecardHub),
            switchMap(() => (this._identityService.enforceOAuth ? this._identityService.isAuthenticated$ : of(true))),
            filter((auth) => auth),
            switchMap(() =>
                this._hubConnectionService.timecardHub.startConnection().pipe(
                    map(() => HubActions.connectToTimecardHubSuccess()),
                    catchError((error) => of(HubActions.connectToTimecardHubFailure({ error })))
                )
            )
        );
    });

    disconnectFromTimecardHub$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(HubActions.disconnectFromTimecardHub),
            switchMap(() =>
                this._hubConnectionService.timecardHub.stopConnection().pipe(
                    map(() => HubActions.disconnectFromTimecardHubSuccess()),
                    catchError((error) => of(HubActions.disconnectFromTimecardHubFailure({ error })))
                )
            )
        );
    });

    isTimecardHubConnectionActive$ = createEffect(() => {
        return this._hubConnectionService.timecardHub
            .isConnectionActive()
            .pipe(map((isActive) => HubActions.isTimecardHubConnectionActive({ isActive })));
    });

    connectToInvoiceHub$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(HubActions.connectToInvoiceHub),
            switchMap(() => (this._identityService.enforceOAuth ? this._identityService.isAuthenticated$ : of(true))),
            filter((auth) => auth),
            switchMap(() =>
                this._hubConnectionService.invoiceHub.startConnection().pipe(
                    map(() => HubActions.connectToInvoiceHubSuccess()),
                    catchError((error) => of(HubActions.connectToInvoiceHubFailure({ error })))
                )
            )
        );
    });

    disconnectFromInvoiceHub$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(HubActions.disconnectFromInvoiceHub),
            switchMap(() =>
                this._hubConnectionService.invoiceHub.stopConnection().pipe(
                    map(() => HubActions.disconnectFromInvoiceHubSuccess()),
                    catchError((error) => of(HubActions.disconnectFromInvoiceHubFailure({ error })))
                )
            )
        );
    });

    isInvoiceHubConnectionActive$ = createEffect(() => {
        return this._hubConnectionService.invoiceHub
            .isConnectionActive()
            .pipe(map((isActive) => HubActions.isInvoiceHubConnectionActive({ isActive })));
    });

    connectToReverseInvoiceHub$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(HubActions.connectToReverseInvoiceHub),
            switchMap(() => (this._identityService.enforceOAuth ? this._identityService.isAuthenticated$ : of(true))),
            filter((auth) => auth),
            switchMap(() =>
                this._hubConnectionService.reverseInvoiceHub.startConnection().pipe(
                    map(() => HubActions.connectToReverseInvoiceHubSuccess()),
                    catchError((error) => of(HubActions.connectToReverseInvoiceHubFailure({ error })))
                )
            )
        );
    });

    disconnectFromReverseInvoiceHub$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(HubActions.disconnectFromReverseInvoiceHub),
            switchMap(() =>
                this._hubConnectionService.reverseInvoiceHub.stopConnection().pipe(
                    map(() => HubActions.disconnectFromReverseInvoiceHubSuccess()),
                    catchError((error) => of(HubActions.disconnectFromReverseInvoiceHubFailure({ error })))
                )
            )
        );
    });

    isReverseInvoiceHubConnectionActive$ = createEffect(() => {
        return this._hubConnectionService.reverseInvoiceHub
            .isConnectionActive()
            .pipe(map((isActive) => HubActions.isReverseInvoiceHubConnectionActive({ isActive })));
    });

    connectToComplianceHub$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(HubActions.connectToComplianceHub),
            switchMap((action) => {
                if (this._identityService.enforceOAuth) {
                    return this._identityService.isAuthenticated$.pipe(
                        filter((auth) => auth),
                        map(() => action)
                    );
                } else {
                    return of(action);
                }
            }),
            switchMap((action) =>
                this._hubConnectionService.complianceReportHub
                    .startConnection([`CR.AutoMatch.Contract.${action.contractId}.Group`])
                    .pipe(
                        map(() => HubActions.connectToComplianceHubSuccess()),
                        catchError((error) => of(HubActions.connectToComplianceHubFailure({ error })))
                    )
            )
        );
    });

    disconnectFromComplianceHub$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(HubActions.disconnectFromComplianceHub),
            switchMap(() =>
                this._hubConnectionService.complianceReportHub.stopConnection().pipe(
                    map(() => HubActions.disconnectFromComplianceHubSuccess()),
                    catchError((error) => of(HubActions.disconnectFromComplianceHubFailure({ error })))
                )
            )
        );
    });

    isComplianceHubConnectionActive$ = createEffect(() => {
        return this._hubConnectionService.complianceReportHub
            .isConnectionActive()
            .pipe(map((isActive) => HubActions.isComplianceHubConnectionActive({ isActive })));
    });
}
